import React, { useState, useEffect } from 'react';
import seedrandom from 'seedrandom';
import { TRAIL_LIST } from '../components/SectionSwitch/constants';
import { SearchFiltersUtil } from '../utils/search_filters_util';
import { isFavorite, isComplete, isVerified } from '../microapps/Community/utils/lists_helper';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { getSearchIndex } from '../utils/search';

const sortFilters = {
  best_match: {
    'best-match': {
      name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.BEST_MATCH,
      direction: 0,
      selected: true
    }
  },
  most_popular: {
    'most-popular': {
      name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.MOST_POPULAR,
      direction: 0,
      selected: true
    }
  },
  closest: {
    closest: {
      name: StringConstantsMixin.strings.FILTERS.SORT_TYPES.CLOSEST,
      direction: 0,
      selected: true
    }
  }
};

const aroundRadius = 48270; // 30 miles in meters
let algoliaSearch = null;

const getOrInitializeSearchIndex = indexId => {
  if (!algoliaSearch) {
    algoliaSearch = getSearchIndex(indexId);
  }
  return algoliaSearch;
};

const MAX_RADIUS_MULTIPLIER = 3;
const HITS_LIMIT = 11;
const CARD_ATTRIBUTES = [
  'ID',
  'area_id',
  'area_slug',
  'avg_rating',
  'city_id',
  'city_name',
  'city_url',
  'country_id',
  'country_name',
  'difficulty_rating',
  'duration_minutes',
  'length',
  'name',
  'num_reviews',
  'park_name',
  'park_slug',
  'popularity',
  'profile_photo_data',
  'slug',
  'state_id',
  'state_name',
  'type'
];
const RANDOMNESS_FACTOR = 20; // more random = more shuffling, but potentially less ideal top results

const useSections = (initialSections, latLng, context, listItems) => {
  const [sections, updateSections] = useState(initialSections);
  const [searchRadiusModifier, updateSearchRadiusModifier] = useState(1);
  const [lat, lng] = latLng;

  const getTrailCarouselSections = () => {
    return sections.filter(s => s.section_type === TRAIL_LIST);
  };

  const parseFilter = filter => {
    // here we need to parse the filters to map to what the server has given us
    const filterObj = SearchFiltersUtil.getSearchFilters(context);
    filterObj.sort = sortFilters[filter.sort];

    // server says suitability, client calls it access
    if (Object.prototype.hasOwnProperty.call(filter, 'suitability')) {
      filter.suitability.forEach(suitability => {
        filterObj.access[suitability].selected = true;
      });
    }

    if (Object.prototype.hasOwnProperty.call(filter, 'feature')) {
      filter.feature.forEach(feature => {
        filterObj.features[feature].selected = true;
      });
    }

    filterObj.lengths.value = [filter.length.min, filter.length.max];

    filterObj.has_profile_photo = true;

    filterObj.minRating = 4;

    // this object is ready to communicate with the Algolia API
    return filterObj;
  };

  const createAlgoliaObject = (filters, idx = 0) => {
    const algoliaObject = SearchFiltersUtil.createAlgoliaQueryObject({
      filters,
      mobileWidth: false,
      page: 'home',
      verifiedIds: [],
      completedIds: []
    });
    return Object.assign(algoliaObject, {
      hitsPerPage: HITS_LIMIT * (idx + 1) + Math.ceil(HITS_LIMIT/2),
      aroundLatLng: `${lat},${lng}`,
      aroundRadius: aroundRadius * searchRadiusModifier,
      attributesToRetrieve: CARD_ATTRIBUTES
    });
  };

  const handleBatchSearchSuccess = ({ results }, filters) => {
    const isViable = results.some(result => result.hits.length >= 8); // any river has at least 8 results
    if (!isViable && searchRadiusModifier < MAX_RADIUS_MULTIPLIER) {
      updateSearchRadiusModifier(searchRadiusModifier + 1);
      return;
    }

    const now = new Date();
    const today = [now.getFullYear(), now.getMonth(), now.getDate()].join('-');
    const stickyRandomNumber = seedrandom(today); // same random numbers generated throughout the day
    const getScore = trail => {
      return trail.popularity + trail.avg_rating * 4 + stickyRandomNumber() * RANDOMNESS_FACTOR;
    };

    let resultIndex = 0;
    let hits;
    const hitIds = [];
    const resultLimiter = results.length - 1;
    // this callback maps the search results to the rows that have TRAIL_LIST
    // we can only have a top limit of 12 and we cannot have repeat trail cards between sections
    updateSections(prevSections =>
      prevSections
        .map(section => {
          if (section.section_type !== TRAIL_LIST || resultIndex > resultLimiter) {
            return section;
          }

          hits = [];

          const trailsSortedByPopularity = [...results[resultIndex].hits].sort((a, b) => b.popularity - a.popularity);

          // Take the top HITS_LIMIT trails that haven't been displayed yet
          trailsSortedByPopularity.forEach(hit => {
            if (!hitIds.includes(hit.ID) && hits.length < HITS_LIMIT) {
              hit.isCompleted = isComplete(hit.type, hit.ID, listItems);
              hit.isFavorite = isFavorite(hit.type, hit.ID, listItems);
              hit.isVerified = isVerified(hit.type, hit.ID, listItems);
              hits.push(hit);
              hitIds.push(hit.ID);
            }
          });

          hits.sort((a, b) => {
            return getScore(b) - getScore(a);
          });

          hits.sort((a, b) => {
            // Move completed and verified completed trails to the end
            return Number(a.isCompleted || a.isVerified) - Number(b.isCompleted || b.isVerified)
          });

          hits.push({
            exploreMoreUrl: `${SearchFiltersUtil.createUrlFromPageAndFilters('EXPLORE', filters[resultIndex])}?ref=homepage_explore_showmorecard`
          });

          resultIndex += 1;
          return { ...section, hits };
        })
        .filter(section => {
          if (section.section_type !== TRAIL_LIST) {
            return true;
          }
          // if the section has less than 3 hits we filter out the section
          return section.hits && section.hits.length > 3;
        })
    );
  };

  const generateTrailListFilters = filters => {
    let parsedFilter;
    const parsedFilters = [];

    const algoliaObjects = filters.map((filter, idx) => {
      parsedFilter = parseFilter(filter);
      parsedFilters.push(parsedFilter);
      return createAlgoliaObject(parsedFilter, idx);
    });

    getOrInitializeSearchIndex(context.primaryAlgoliaIndexId)
      .performBatchSearch(algoliaObjects)
      .then(response => handleBatchSearchSuccess(response, parsedFilters))
      .catch(err => {
        console.error('err', err);
      });
  };

  useEffect(() => {
    const initialFilters = getTrailCarouselSections().map(list => list.filters);
    generateTrailListFilters(initialFilters);
  }, [searchRadiusModifier]);

  return sections;
};

export default useSections;
